import type { DeductionKind } from "@/api/deduction.tsx"

/** Portal display sign: deductions negative, invoices/repayments positive, prepayments as stored. */
/**
 * What a UNFI `PP` prepayment row is and why it is (mostly) not codeable, for the coding UI.
 * Mirrors `Deduction::gl_coding_block_reason` on the server: cash is never coded; a priced advance
 * codes its terms discount only. `null` for anything that is not a UNFI prepayment.
 */
export function prepayment_coding_notice(transaction: {
  source?: string
  kind: DeductionKind
  invoice_number: string
  invoice_amount: number
  discount_amount?: number | null
}): { blocked: boolean; message: string } | null {
  if (transaction.source !== "unfi" || transaction.kind !== "prepayment") {
    return null
  }
  const discount = Math.abs(transaction.discount_amount ?? 0)
  if (transaction.invoice_amount < 0) {
    return {
      blocked: true,
      message:
        `${transaction.invoice_number} is UNFI taking back an early payment (PP) it made on an earlier check. ` +
        `It is cash, not spend: it can't be coded to a GL account, and cash application settles it against the invoice. ` +
        `If UNFI re-listed the invoice for less than it took back, the difference appears on this check as its own deduction row — that is the row to code.`,
    }
  }
  if (discount > 0) {
    return {
      blocked: false,
      message:
        `${transaction.invoice_number} is UNFI paying an invoice early (PP), net of the terms discount. ` +
        `Only the ${discount.toFixed(2)} discount can be coded here; the cash is applied to the invoice in cash application.`,
    }
  }
  return {
    blocked: true,
    message:
      `${transaction.invoice_number} is UNFI paying an invoice early (PP), net of the terms discount. ` +
      `It is cash, not spend: it can't be coded to a GL account. Once the invoice is matched, its discount codes automatically.`,
  }
}

/**
 * An invoice paid with no early-pay discount has nothing to code: its coding amount is zero and the
 * server already reads it as coded (`coding_status`). Say so instead of offering a form whose only
 * possible save is a $0.00 line. `null` for anything with an amount to code.
 */
export function nothing_to_code_notice(transaction: {
  kind: DeductionKind
  invoice_number: string
  discount_amount?: number | null
}): string | null {
  if (transaction.kind !== "invoice" || Math.round(Math.abs(transaction.discount_amount ?? 0) * 100) !== 0) {
    return null
  }
  return (
    `${transaction.invoice_number} was paid with no early-pay discount, so there is nothing to code. ` +
    `It already counts as coded; the cash is applied to the invoice in cash application.`
  )
}

/**
 * Why a row should get no "+ Code" pill or bulk-coding line at all: an invoice paid with no
 * discount (nothing to code), or a UNFI prepayment whose cash the server refuses to code
 * (`Deduction::gl_coding_block_reason`). The one rule for every quick-code surface — the check
 * page's pill, the coding strip's "uncoded" count and the bulk "fully code" body — so a PP row
 * never offers a pill that can only fail. Compostic's 360143: the `SI00004145PP` reversal sat
 * beside the `SI00004145` clawback row that actually needed coding, and the pill on the PP row
 * returned the server's refusal instead of pointing at the row below it. `null` when codeable.
 */
export function coding_pill_notice(transaction: {
  source?: string
  kind: DeductionKind
  invoice_number: string
  invoice_amount: number
  discount_amount?: number | null
}): string | null {
  const nothing = nothing_to_code_notice(transaction)
  if (nothing) {
    return nothing
  }
  const prepayment = prepayment_coding_notice(transaction)
  if (prepayment?.blocked) {
    return prepayment.message
  }
  return null
}

export function display_transaction_amount(kind: DeductionKind, amount: number): number {
  if (kind === "deduction") {
    return -Math.abs(amount)
  }
  if (kind === "invoice" || kind === "repayment") {
    return Math.abs(amount)
  }
  return amount
}

/**
 * Magnitude a dispute claims by default. Mirrors `Deduction::disputable_amount` on the server: a
 * UNFI adjustment whose remittance line netted the terms discount back out (a shortage listed as
 * gross -164.00 / discount +3.28 / net -160.72, stored with the net in `invoice_amount`) is claimed
 * at its gross, because that is what the portal adjustment withheld. Everything else claims the
 * amount taken. GL coding deliberately still targets the net (see `coding_amount`).
 */
export function disputable_amount(deduction: {
  source: string
  kind: DeductionKind
  invoice_amount: number
  gross_amount?: number | null
  discount_amount?: number | null
}): number {
  const discounted = Math.abs(deduction.discount_amount ?? 0) > 0
  const gross = Math.abs(deduction.gross_amount ?? 0)
  if (deduction.source === "unfi" && deduction.kind === "deduction" && discounted && gross > 0) {
    return gross
  }
  return Math.abs(deduction.invoice_amount)
}

/**
 * Amount that should be coded to a GL account for a transaction. Early-pay invoices book only the
 * discount taken (the rest is settled by cash); every other kind books the full transaction amount.
 * Always returned as a positive magnitude.
 */
export function coding_amount(kind: DeductionKind, invoice_amount: number, discount_amount: number | null | undefined): number {
  if (kind === "invoice") {
    return Math.abs(discount_amount ?? 0)
  }
  return Math.abs(invoice_amount)
}

/**
 * The amount to display for a transaction in coding views. Early-pay invoices show their discount
 * (the amount actually booked) as a negative, because the discount is a cost to us — it books as a
 * debit to Early Payment Discounts, exactly like every other deduction line on the check. Every
 * other kind shows its real signed amount — a normal deduction is negative (money taken), while a
 * positive-amount transaction (a reversal/tax credit like KeHE's `BTTAX*`, or a repayment where the
 * customer pays money back) shows positive. We do NOT force deductions negative, because the sign of
 * `invoice_amount` is meaningful in the accounting flow.
 */
export function display_coding_amount(kind: DeductionKind, invoice_amount: number, discount_amount: number | null | undefined): number {
  if (kind === "invoice") {
    const discount = Math.abs(discount_amount ?? 0)
    // Avoid returning -0, which formats as "-$0.00".
    return discount === 0 ? 0 : -discount
  }
  return invoice_amount
}

/**
 * The amount to display for a transaction on the accounting transaction detail page, where the
 * `invoice_amount` sign is meaningful. Invoices show their full positive amount (the discount is shown
 * separately); every other kind keeps its real sign so positive-amount reversals/credits (e.g. KeHE
 * `BTTAX*`) aren't forced negative just because the kind is "deduction".
 */
export function display_accounting_amount(kind: DeductionKind, invoice_amount: number): number {
  return kind === "invoice" ? Math.abs(invoice_amount) : invoice_amount
}

/**
 * Display amount for a deduction-list row, which carries a separate always-positive `deduction_amount`
 * magnitude (dispute/split-aware). We keep that magnitude but restore the real sign from
 * `invoice_amount`: a normal deduction (negative `invoice_amount`) shows negative, while a
 * positive-amount reversal/credit (e.g. KeHE `BTTAX*`) shows positive. Invoices and repayments read
 * positive.
 */
export function display_row_amount(kind: DeductionKind, invoice_amount: number, deduction_amount: number): number {
  const magnitude = Math.abs(deduction_amount)
  return kind === "deduction" && invoice_amount < 0 ? -magnitude : magnitude
}

/**
 * Cents still needing assignment when coding a transaction. Compares the *magnitude* of the split sum
 * to the magnitude of the transaction amount, so a positive-amount deduction (e.g. KeHE `BTTAX*`) coded
 * with negative splits — or vice versa — still reads as fully coded when the amounts match. Returns
 * `> 0` when over-assigned, `< 0` when under-assigned, `0` when exact.
 */
export function split_uncoded_cents(transaction_amount: number, total_split_cents: number): number {
  return Math.abs(total_split_cents) - Math.round(Math.abs(transaction_amount) * 100)
}

/**
 * Sign a split on this transaction must carry — the *opposite* of the stored amount. A deduction is
 * stored negative and codes positive (a cost), so a repayment, stored positive because the customer
 * is giving money back, codes negative; a positive-amount `deduction` row (KeHE `BTTAX*`) follows the
 * same rule. Invoices code their early-pay discount, a cost, so always positive.
 *
 * Mirrors `Deduction::signed_coding_amount` on the server. Anything that *generates* a split amount
 * must apply this instead of taking a magnitude — coding a repayment positive makes it add to trade
 * spend on every report that sums split amounts.
 */
export function split_sign(kind: DeductionKind, invoice_amount: number): 1 | -1 {
  if (kind === "invoice") return 1
  return invoice_amount > 0 ? -1 : 1
}

/** [`coding_amount`] carrying the sign a split on this transaction must have — see [`split_sign`]. */
export function signed_coding_amount(
  kind: DeductionKind,
  invoice_amount: number,
  discount_amount: number | null | undefined
): number {
  const amount = coding_amount(kind, invoice_amount, discount_amount)
  // Avoid returning -0, which formats as "-$0.00".
  return amount === 0 ? 0 : split_sign(kind, invoice_amount) * amount
}

/**
 * Cents a new split should default to so the transaction's coding target closes. Deductions keep
 * the historical sign convention (splits opposite the stored amount); invoices code their
 * discount as a positive amount.
 */
export function remaining_split_cents(
  kind: DeductionKind,
  invoice_amount: number,
  discount_amount: number | null | undefined,
  total_split_cents: number
): number {
  if (kind === "invoice") {
    return Math.round(Math.abs(discount_amount ?? 0) * 100) - total_split_cents
  }
  return -Math.round(invoice_amount * 100) - total_split_cents
}

/** Magnitude for backup-vs-deduction comparison when signs may differ. */
export function backup_deduction_amount_diff(backup_total: number, invoice_amount: number): number {
  return Math.abs(Math.abs(backup_total) - Math.abs(invoice_amount))
}

export function wakefern_backup_match_status(
  backup_total: number,
  invoice_amount: number | null | undefined
): "match" | "close" | "mismatch" | null {
  if (invoice_amount == null) {
    return null
  }
  const diff = backup_deduction_amount_diff(backup_total, invoice_amount)
  if (diff < 0.01) {
    return "match"
  }
  if (diff < 1.0) {
    return "close"
  }
  return "mismatch"
}

/** Backup total shown alongside a deduction (same visual sign as deduction display). */
export function display_backup_total_for_deduction(backup_total: number): number {
  return -Math.abs(backup_total)
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("display_transaction_amount", () => {
    it("shows deductions as negative regardless of stored sign", () => {
      expect(display_transaction_amount("deduction", 7162.23)).toBe(-7162.23)
      expect(display_transaction_amount("deduction", -7162.23)).toBe(-7162.23)
    })

    it("shows invoices and repayments as positive", () => {
      expect(display_transaction_amount("invoice", -100)).toBe(100)
      expect(display_transaction_amount("repayment", -50)).toBe(50)
    })

    it("explains UNFI prepayments and blocks coding their cash", () => {
      const reversal = { source: "unfi", kind: "prepayment" as DeductionKind, invoice_number: "SI00003888PP", invoice_amount: -6727.13 }
      expect(prepayment_coding_notice(reversal)?.blocked).toBe(true)
      expect(prepayment_coding_notice(reversal)?.message).toContain("taking back")
      const priced = { ...reversal, invoice_number: "SI00004019PP", invoice_amount: 10282, discount_amount: 209.84 }
      expect(prepayment_coding_notice(priced)).toEqual({ blocked: false, message: expect.stringContaining("209.84 discount") })
      const unpriced = { ...priced, discount_amount: null }
      expect(prepayment_coding_notice(unpriced)?.blocked).toBe(true)
      expect(prepayment_coding_notice({ ...reversal, source: "promomash" })).toBeNull()
      expect(prepayment_coding_notice({ ...reversal, kind: "deduction" as DeductionKind })).toBeNull()
    })

    it("passes prepayment amounts through unchanged", () => {
      expect(display_transaction_amount("prepayment", -50)).toBe(-50)
      expect(display_transaction_amount("prepayment", 50)).toBe(50)
    })
  })

  describe("coding_pill_notice", () => {
    it("hides the pill on a UNFI PP reversal but not on the clawback row beside it", () => {
      const reversal = { source: "unfi", kind: "prepayment" as DeductionKind, invoice_number: "SI00004145PP", invoice_amount: -5214.74 }
      expect(coding_pill_notice(reversal)).toContain("taking back")
      const clawback = { ...reversal, kind: "deduction" as DeductionKind, invoice_number: "SI00004145" }
      expect(coding_pill_notice(clawback)).toBeNull()
    })

    it("still offers the pill on a priced advance, whose discount is codeable", () => {
      const priced = { source: "unfi", kind: "prepayment" as DeductionKind, invoice_number: "SI00004019PP", invoice_amount: 10282, discount_amount: 209.84 }
      expect(coding_pill_notice(priced)).toBeNull()
    })

    it("keeps the nothing-to-code rule for undiscounted invoices", () => {
      expect(coding_pill_notice({ kind: "invoice" as DeductionKind, invoice_number: "INV-1", invoice_amount: 100, discount_amount: 0 })).toContain("nothing to code")
      expect(coding_pill_notice({ kind: "invoice" as DeductionKind, invoice_number: "INV-1", invoice_amount: 98, discount_amount: 2 })).toBeNull()
    })
  })

  describe("disputable_amount", () => {
    // Saffron Road's UNFI shortage INV075319: gross -164.00, discount +3.28, net -160.72.
    const shortage = { source: "unfi", kind: "deduction" as DeductionKind, invoice_amount: -160.72, gross_amount: 164, discount_amount: -3.28 }

    it("claims the gross of a UNFI adjustment that netted a terms discount", () => {
      expect(disputable_amount(shortage)).toBe(164)
      // The manual-CSV import stores the gross negative; only the magnitude matters.
      expect(disputable_amount({ ...shortage, gross_amount: -164 })).toBe(164)
    })

    it("claims the amount taken everywhere else", () => {
      expect(disputable_amount({ ...shortage, discount_amount: null })).toBe(160.72)
      expect(disputable_amount({ ...shortage, gross_amount: null })).toBe(160.72)
      expect(disputable_amount({ ...shortage, source: "kehe" })).toBe(160.72)
      expect(disputable_amount({ ...shortage, kind: "invoice" })).toBe(160.72)
    })
  })

  describe("nothing_to_code_notice", () => {
    it("flags an invoice paid with no early-pay discount", () => {
      expect(nothing_to_code_notice({ kind: "invoice", invoice_number: "24531", discount_amount: 0 })).toContain("24531")
      expect(nothing_to_code_notice({ kind: "invoice", invoice_number: "24531", discount_amount: null })).not.toBeNull()
      expect(nothing_to_code_notice({ kind: "invoice", invoice_number: "24531" })).not.toBeNull()
    })

    it("leaves a discounted invoice and every other kind codeable", () => {
      expect(nothing_to_code_notice({ kind: "invoice", invoice_number: "INV1", discount_amount: -20 })).toBeNull()
      expect(nothing_to_code_notice({ kind: "invoice", invoice_number: "INV1", discount_amount: 0.01 })).toBeNull()
      expect(nothing_to_code_notice({ kind: "deduction", invoice_number: "D1", discount_amount: 0 })).toBeNull()
      expect(nothing_to_code_notice({ kind: "repayment", invoice_number: "R1" })).toBeNull()
    })
  })

  describe("coding_amount", () => {
    it("codes only the discount for early-pay invoices", () => {
      expect(coding_amount("invoice", 1000, 20)).toBe(20)
      expect(coding_amount("invoice", -1000, -20)).toBe(20)
    })

    it("treats a missing invoice discount as zero", () => {
      expect(coding_amount("invoice", 1000, null)).toBe(0)
      expect(coding_amount("invoice", 1000, undefined)).toBe(0)
    })

    it("codes the full amount for non-invoice kinds", () => {
      expect(coding_amount("deduction", -75, null)).toBe(75)
      expect(coding_amount("repayment", 50, undefined)).toBe(50)
    })
  })

  describe("display_coding_amount", () => {
    it("shows the discount as a negative (a cost) for early-pay invoices", () => {
      expect(display_coding_amount("invoice", 2696.8, 53.94)).toBe(-53.94)
      expect(display_coding_amount("invoice", 2696.8, -53.94)).toBe(-53.94)
    })

    it("treats a missing invoice discount as zero", () => {
      expect(display_coding_amount("invoice", 2696.8, null)).toBe(0)
      expect(display_coding_amount("invoice", 2696.8, undefined)).toBe(0)
    })

    it("respects the real sign for non-invoice kinds", () => {
      // A normal deduction is stored negative; show it negative.
      expect(display_coding_amount("deduction", -493.11, null)).toBe(-493.11)
      // A positive-amount deduction (e.g. a BTTAX reversal) shows positive, not forced negative.
      expect(display_coding_amount("deduction", 67.2, null)).toBe(67.2)
      expect(display_coding_amount("repayment", 69.59, undefined)).toBe(69.59)
    })
  })

  describe("display_accounting_amount", () => {
    it("shows invoices as a positive full amount", () => {
      expect(display_accounting_amount("invoice", -2696.8)).toBe(2696.8)
      expect(display_accounting_amount("invoice", 2696.8)).toBe(2696.8)
    })

    it("keeps the real sign for deductions and repayments", () => {
      expect(display_accounting_amount("deduction", -450)).toBe(-450)
      expect(display_accounting_amount("deduction", 228)).toBe(228)
      expect(display_accounting_amount("repayment", 1094.65)).toBe(1094.65)
    })
  })

  describe("display_row_amount", () => {
    it("keeps the deduction_amount magnitude but signs it from invoice_amount", () => {
      // Normal deduction: invoice_amount negative → shown negative.
      expect(display_row_amount("deduction", -450, 450)).toBe(-450)
      // BTTAX reversal: positive invoice_amount → shown positive.
      expect(display_row_amount("deduction", 3.6, 3.6)).toBe(3.6)
    })

    it("preserves a dispute/split magnitude that differs from invoice_amount", () => {
      // deduction_amount (200) is a partial dispute amount, invoice_amount is -450.
      expect(display_row_amount("deduction", -450, 200)).toBe(-200)
    })

    it("shows invoices and repayments positive", () => {
      expect(display_row_amount("invoice", 1000, 20)).toBe(20)
      expect(display_row_amount("repayment", 1094.65, 1094.65)).toBe(1094.65)
    })
  })

  describe("split_uncoded_cents", () => {
    it("treats a negative split as fully coding a positive-amount deduction", () => {
      // BTTAX: invoice_amount +354, split -354 → fully coded (0 remaining).
      expect(split_uncoded_cents(354, -35400)).toBe(0)
    })

    it("treats a positive split as fully coding a normal (negative) deduction", () => {
      expect(split_uncoded_cents(-450, 45000)).toBe(0)
    })

    it("reports under- and over-assignment by magnitude", () => {
      expect(split_uncoded_cents(354, -20000)).toBe(-15400) // under-assigned
      expect(split_uncoded_cents(354, -40000)).toBe(4600) // over-assigned
    })
  })

  describe("remaining_split_cents", () => {
    it("defaults an invoice's new split to its uncovered discount", () => {
      expect(remaining_split_cents("invoice", 4469.76, 89.4, 0)).toBe(8940)
      expect(remaining_split_cents("invoice", 4469.76, 89.4, 5000)).toBe(3940)
      expect(remaining_split_cents("invoice", 4469.76, null, 0)).toBe(0)
    })

    it("keeps the opposite-sign convention for deductions", () => {
      expect(remaining_split_cents("deduction", -450, null, 0)).toBe(45000)
      expect(remaining_split_cents("deduction", -450, null, 20000)).toBe(25000)
      // Positive-amount deduction (BTTAX) codes with a negative split.
      expect(remaining_split_cents("deduction", 354, null, 0)).toBe(-35400)
    })
  })

  describe("split_sign", () => {
    it("opposes the stored amount so repayments code negative", () => {
      expect(split_sign("deduction", -814.13)).toBe(1)
      // UNFI repaying a spoils deduction — stored positive, codes negative.
      expect(split_sign("repayment", 119.55)).toBe(-1)
      // Positive-amount deduction (KeHE BTTAX) follows the same rule.
      expect(split_sign("deduction", 354)).toBe(-1)
      // Invoices code the early-pay discount, which is a cost either way.
      expect(split_sign("invoice", 24728.57)).toBe(1)
    })

    it("agrees with remaining_split_cents", () => {
      for (const [kind, amount] of [
        ["deduction", -450],
        ["deduction", 354],
        ["repayment", 119.55],
      ] as const) {
        expect(Math.sign(remaining_split_cents(kind, amount, null, 0))).toBe(split_sign(kind, amount))
      }
    })
  })

  describe("backup_deduction_amount_diff", () => {
    it("matches opposite-signed backup and deduction magnitudes", () => {
      expect(backup_deduction_amount_diff(-7162.23, -7162.23)).toBe(0)
      expect(backup_deduction_amount_diff(-7162.23, 7162.23)).toBe(0)
    })
  })

  describe("wakefern_backup_match_status", () => {
    it("returns match for screenshot scenario", () => {
      expect(wakefern_backup_match_status(-7162.23, -7162.23)).toBe("match")
    })
  })
}
